import type { UnwrapRef } from 'vue';
import { useThrottleFn } from '@vueuse/core';

/**
 * useSmartDataRefresh
 * * Automatically refreshes Nuxt data (useFetch/useAsyncData) when the user
 * returns to the app after being idle or backgrounded for a set period.
 */
export const useSmartDataRefresh = (
  options = {
    thresholdMin: 10, // How many minutes of idleness before a refresh is triggered
    useJitter: true, // Randomize refresh timing to prevent server spikes
    checkInactivity: true, // Also track mouse/keyboard idleness, not just tab visibility
  },
): {isRefreshing: Ref<UnwrapRef<boolean>, UnwrapRef<boolean> | boolean>}  => {

  // --- State ---
  const THRESHOLD_MS = options.thresholdMin * 60 * 1000;
  const lastActiveTime = ref(Date.now());
  const isRefreshing = ref(false);
  let heartbeatInterval: any = null;

  /**
   * The core refresh logic.
   * Uses Nuxt's internal refresh mechanism to avoid a full page reload.
   */
  const performRefresh = async () => {
    // Safety check: Don't refresh if we are already refreshing or offline
    if (isRefreshing.value || !navigator.onLine) return;

    isRefreshing.value = true;

    // JITTER: If 100 users open their laptops at once, this spreads the
    // load over 3 seconds so the API doesn't crash.
    if (options.useJitter) {
      await new Promise((resolve) => setTimeout(resolve, Math.random() * 3000));
    }

    try {

      clearNuxtData();
      window.location.reload();

      // this will reload the page, but will keep old bundle data (if we do a deploy)
      // const currentFullUrl =
      //   window.location.pathname + window.location.search + window.location.hash;
      // reloadNuxtApp({
      //   path: currentFullUrl,
      //   persistState: false,
      // });

    } catch (err) {
      console.error('Auto-refresh failed:', err);
    } finally {
      isRefreshing.value = false;
      lastActiveTime.value = Date.now(); // Reset timer after success
    }
  };

  /**
   * Compares the current time against the last recorded activity.
   */
  const checkElapsed = () => {
    const now = Date.now();
    if (now - lastActiveTime.value >= THRESHOLD_MS) {
      performRefresh();
    } else {
      // If we haven't hit the threshold, just update the tick
      lastActiveTime.value = now;
    }
  };

  /**
   * Event Handler: Tab visibility (Minimizing browser, switching tabs)
   */
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkElapsed();
    }
  };

  /**
   * Event Handler: Physical user activity (Mouse, Keys, Touch)
   */
  const updateActivity = () => {
    // Only update if we aren't currently stale.
    // This prevents activity from "resetting" a refresh that should have happened.
    const now = Date.now();
    if (now - lastActiveTime.value < THRESHOLD_MS) {
      lastActiveTime.value = now;
    }
  };

  const throttledUpdateActivity = useThrottleFn(updateActivity, 125);

  // --- Lifecycle Hooks ---
  onMounted(() => {
    // Listen for tab switching/mobile app backgrounding
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Listen for physical interaction to define "active"
    if (options.checkInactivity) {
      const events = ['mousemove', 'keydown', 'scroll', 'touchstart'];
      events.forEach((e) => window.addEventListener(e, throttledUpdateActivity, { passive: true }));
    }

    /**
     * HEARTBEAT: This is the most robust part.
     * If a laptop lid is closed, JS execution stops. When it opens,
     * this interval triggers. If the time jump is > THRESHOLD, we know
     * the system was asleep and we need fresh data.
     */
    heartbeatInterval = setInterval(() => {
      const now = Date.now();
      if (now - lastActiveTime.value > THRESHOLD_MS) {
        performRefresh();
      }
    }, 15000); // Check every 15 seconds
  });

  onUnmounted(() => {
    // Clean up to prevent memory leaks and ghost execution
    document.removeEventListener('visibilitychange', onVisibilityChange);
    const events = ['mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.removeEventListener(e, throttledUpdateActivity));
    clearInterval(heartbeatInterval);
  });

  return {
    isRefreshing, // Export this so the UI can show a "Syncing..." spinner
  };
};
